import { defineStore } from 'pinia'
import type { MappedGrid } from '@wangdandan810012/bead-core'
import { cloneGrid } from '@wangdandan810012/bead-core'
import type { ColorRemap, ProjectParams } from '../types/app.js'
import { DEFAULT_PARAMS } from '../types/app.js'

interface ProjectState {
  sourcePath: string
  sourceWidth: number
  sourceHeight: number
  sourcePixels: Uint8ClampedArray | null
  sourcePreview: string
  grid: MappedGrid | null
  params: ProjectParams
  excludedPaletteIds: string[]
  colorRemaps: ColorRemap[]
  dirty: boolean
  projectName: string
  savedProjectId: string | null
}

export const useProjectStore = defineStore('project', {
  state: (): ProjectState => ({
    sourcePath: '',
    sourceWidth: 0,
    sourceHeight: 0,
    sourcePixels: null,
    sourcePreview: '',
    grid: null,
    params: { ...DEFAULT_PARAMS },
    excludedPaletteIds: [],
    colorRemaps: [],
    dirty: false,
    projectName: '未命名项目',
    savedProjectId: null,
  }),
  getters: {
    hasGrid: (state) => !!state.grid && state.grid.length > 0,
    gridSize: (state) => ({
      width: state.grid?.[0]?.length ?? 0,
      height: state.grid?.length ?? 0,
    }),
  },
  actions: {
    setSource(path: string, width: number, height: number, pixels: Uint8ClampedArray) {
      this.sourcePath = path
      this.sourceWidth = width
      this.sourceHeight = height
      this.sourcePixels = pixels
      this.savedProjectId = null
      this.dirty = true
    },
    setSourcePreview(preview: string) {
      this.sourcePreview = preview
    },
    /** 从已保存的预览图恢复像素数据，用于重新生成（不改变保存状态） */
    hydrateSourcePixels(
      width: number,
      height: number,
      pixels: Uint8ClampedArray,
      path?: string,
    ) {
      if (path) this.sourcePath = path
      this.sourceWidth = width
      this.sourceHeight = height
      this.sourcePixels = pixels
    },
    setGrid(grid: MappedGrid) {
      this.grid = cloneGrid(grid)
      this.dirty = true
    },
    updateCell(row: number, col: number, paletteId: string, hex: string) {
      if (!this.grid?.[row]?.[col]) return
      this.grid[row][col] = { paletteId, hex, isExternal: false }
      this.dirty = true
    },
    eraseCell(row: number, col: number, paletteId: string, hex: string) {
      if (!this.grid?.[row]?.[col]) return
      this.grid[row][col] = { paletteId, hex, isExternal: true }
      this.dirty = true
    },
    setParams(params: Partial<ProjectParams>) {
      const next = { ...this.params, ...params }
      const grid = Math.round(Number(next.gridWidth))
      next.gridWidth = Number.isFinite(grid) ? Math.max(1, Math.min(256, grid)) : this.params.gridWidth
      const merge = Number(next.mergeThreshold)
      next.mergeThreshold = Number.isFinite(merge) ? Math.max(0, merge) : 0
      const maxColors = Math.round(Number(next.maxColors))
      next.maxColors = Number.isFinite(maxColors) ? Math.max(0, maxColors) : 0
      next.despeckle = Boolean(next.despeckle)
      this.params = next
      this.dirty = true
    },
    toggleExcluded(paletteId: string) {
      const set = new Set(this.excludedPaletteIds)
      if (set.has(paletteId)) set.delete(paletteId)
      else set.add(paletteId)
      this.excludedPaletteIds = Array.from(set)
      this.dirty = true
    },
    restoreAllExcluded() {
      this.excludedPaletteIds = []
      this.colorRemaps = []
      this.dirty = true
    },
    setColorRemaps(items: Array<{ fromId: string; toId: string }>) {
      const incoming = new Map<string, string>()
      for (const item of items) {
        if (!item.fromId || !item.toId || item.fromId === item.toId) continue
        incoming.set(item.fromId, item.toId)
      }
      if (!incoming.size) return
      this.colorRemaps = [
        ...this.colorRemaps.filter((item) => !incoming.has(item.fromId)),
        ...Array.from(incoming, ([fromId, toId]) => ({ fromId, toId })),
      ]
      this.excludedPaletteIds = this.excludedPaletteIds.filter((id) => !incoming.has(id))
      this.dirty = true
    },
    setColorRemap(fromId: string, toId: string) {
      this.setColorRemaps([{ fromId, toId }])
    },
    loadSnapshot(data: {
      grid: MappedGrid
      params: ProjectParams
      excludedPaletteIds: string[]
      colorRemaps?: ColorRemap[]
      projectName?: string
      sourcePath?: string
      sourcePreview?: string
      savedProjectId?: string | null
    }) {
      this.grid = cloneGrid(data.grid)
      this.params = {
        ...DEFAULT_PARAMS,
        ...data.params,
        imageAdjust: { ...DEFAULT_PARAMS.imageAdjust, ...data.params.imageAdjust },
        photoOptimize: { ...DEFAULT_PARAMS.photoOptimize, ...data.params.photoOptimize },
      }
      this.excludedPaletteIds = [...data.excludedPaletteIds]
      this.colorRemaps = [...(data.colorRemaps ?? [])]
      this.projectName = data.projectName ?? '未命名项目'
      this.sourcePath = data.sourcePath ?? ''
      this.sourcePreview = data.sourcePreview ?? ''
      this.savedProjectId = data.savedProjectId ?? null
      this.sourcePixels = null
      this.dirty = false
    },
    reset() {
      this.$reset()
    },
  },
})
